import { AppProps } from 'next/app';
import Router from 'next/router';
import NProgress from 'nprogress';
import { FunctionComponent, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ThemeProvider } from 'styled-components';

import { getTheme } from '../theme/getTheme';

import { CookieConsent } from '../components/cookie-consent';
import { PreviewBadge } from '../components/preview-badge';
import '../css/main.css';

const queryClient = new QueryClient();

// Only real page changes should reset scroll — same-page navigations that only
// change the query string (e.g. the /connectors filters, search & pagination
// call `router.push({ query })`) must be left exactly where the user was.
const pathnameOf = (url: string) => url.split('?')[0].split('#')[0];
let previousPathname =
  typeof window !== 'undefined' ? window.location.pathname : '';

NProgress.configure({ showSpinner: false });
Router.events.on('routeChangeStart', (url) => {
  NProgress.start();
  // Disable `scroll-behavior: smooth` (css/fixes.css) for real page navigations
  // so the scroll-to-top below is instant, not a slow ~1s animation. Skipped for
  // same-page query changes so their smooth scrolling is untouched.
  if (pathnameOf(url) !== previousPathname) {
    document.documentElement.style.scrollBehavior = 'auto';
  }
});
Router.events.on('routeChangeError', () => {
  NProgress.done();
  document.documentElement.style.scrollBehavior = '';
});
Router.events.on('routeChangeComplete', (url) => {
  NProgress.done();
  const pathname = pathnameOf(url);
  // Same-page (query-only) navigation: leave scroll position and smooth behavior
  // as-is — do not jump to the top.
  if (pathname === previousPathname) {
    return;
  }
  previousPathname = pathname;
  // Reset scroll to the top on a real page change. Two gotchas handled here:
  //   1. The scroll container is the documentElement/window (not <body>), so the
  //      old `document.body.scrollTo` was a no-op and scroll persisted, leaving
  //      pages stranded mid-way after navigating from far down a previous page.
  //   2. The router (scrollRestoration = 'manual') applies its own scroll AFTER
  //      this event, so a synchronous reset loses the race and is ignored. We
  //      defer to the next animation frame, where the reset reliably sticks.
  // Smooth scrolling was disabled in routeChangeStart, so this is an instant
  // jump; it is restored once the page is at the top.
  requestAnimationFrame(() => {
    window.scrollTo(0, 0);
    document.documentElement.style.scrollBehavior = '';
  });
});

const App: FunctionComponent<AppProps> = ({ Component, pageProps }) => {
  // handle #new links to open in new tab without hash
  useEffect(() => {
    function handleClick(event: MouseEvent) {
      const target = event.target as HTMLElement | null;
      const anchor = target?.closest?.('a');

      if (!anchor || !anchor.href || anchor.target === '_blank') {
        return;
      }

      if (anchor?.href?.endsWith('#new')) {
        event.preventDefault();
        window.open(anchor.href.replace(/#new$/, ''), '_blank');
      }
    }

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []);

  return (
    <ThemeProvider theme={getTheme()}>
      <QueryClientProvider client={queryClient}>
        <Component {...pageProps} />
      </QueryClientProvider>
      <CookieConsent />
      <PreviewBadge />
    </ThemeProvider>
  );
};

export default App;
